import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EventCard from '../components/EventCard';
import { NoUpcomingEvents } from '../components/NoUpcomingEvents';
import { getUpcomingEvents } from '../data/events';

const SEO_TITLE = "Upcoming Events — Culture Align by The WE Mentality";
const SEO_DESC = "Live virtual + in-person leadership training and youth entrepreneurship events from The WE Mentality. WE Flight School, Youth CEO Camp, and more.";
const SEO_KEYWORDS = "WE Flight School, Leadership Training, Team Building, Youth Entrepreneurship, Youth CEO Camp, Business Bootcamp, Las Vegas, The WE Mentality, Culture Align";

function setSEO() {
  document.title = SEO_TITLE;
  const set = (name, content, attr = 'name') => {
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  set('description', SEO_DESC);
  set('keywords', SEO_KEYWORDS);
  set('og:title', SEO_TITLE, 'property');
  set('og:description', SEO_DESC, 'property');
  set('og:type', 'website', 'property');
  set('twitter:card', 'summary_large_image');
  set('twitter:title', SEO_TITLE);
  set('twitter:description', SEO_DESC);
}

export default function Events() {
  useEffect(() => {
    setSEO();
    window.scrollTo(0, 0);
  }, []);

  const displayed = getUpcomingEvents();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950" />
        <div className="absolute inset-0 opacity-40">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-violet-600/30 blur-[120px]" />
          <div className="absolute top-16 right-0 w-80 h-80 rounded-full bg-amber-500/20 blur-[100px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors" data-testid="events-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-slate-300 mb-5">
            <CalendarDays className="h-3.5 w-3.5 text-amber-400" />
            {displayed.length} Upcoming
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" data-testid="events-page-title">
            Upcoming <span className="bg-gradient-to-r from-violet-400 via-amber-300 to-violet-400 bg-clip-text text-transparent">Events</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl">
            Develop your leadership, strengthen your team, and prepare for the future through immersive training experiences.
          </p>
        </div>
      </section>

      {/* Events grid */}
      <section className="relative pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8" data-testid="events-grid">
            {displayed.map((event) => (
              <EventCard key={event.slug} event={event} variant="full" />
            ))}
          </div>

          {/* Empty-state safety net */}
          {displayed.length === 0 && <NoUpcomingEvents />}
        </div>
      </section>

      <Footer />
    </div>
  );
}
